import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TopNav } from "./TopNav.js";
import {
  fetchTeam,
  fetchTechnicianDetail,
  patchTechnicianAdmin,
  type TeamSummary,
  type TeamAdminPatch,
} from "../api.js";

// ===========================================================================
// Team admin view — replaces dev.breeze.services/useradmin.php and the
// per-tech customer.php?type=user&id= page admin has been using.
//
// Read-side is the source-of-truth view: list of all technicians, a
// detail panel showing everything the tech-portal Profile page writes
// plus admin-only columns (commission plan, marker colour, etc.).
//
// Write-side intentionally narrow for v1: STATUS, INSURANCE_STATUS,
// NICK_NAME, COMMISSION_PLAN, DEFAULT_LOCATION, MARKER_COLOUR. Tech-
// managed fields stay tech-managed — they round-trip from the portal.
// ===========================================================================

type Props = { userName: string };

const INSURANCE_OPTS = ["Required", "Verifying", "Approved", "Disapproved"] as const;

export function Team({ userName }: Props): JSX.Element {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");

  const teamQ = useQuery({
    queryKey: ["team"],
    queryFn: fetchTeam,
    staleTime: 30_000,
  });

  const filtered = useMemo<TeamSummary[]>(() => {
    const rows = teamQ.data?.technicians ?? [];
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && (r.status ?? "").toLowerCase() !== "active") return false;
      if (s) {
        const hay = [
          r.nickName,
          r.firstName,
          r.lastName,
          r.email,
          r.mobile,
          r.defaultLocation,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [teamQ.data?.technicians, search, showInactive]);

  return (
    <div className="min-h-screen bg-[#0f0f12] text-[#e5e7eb]">
      <TopNav userName={userName} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <header className="flex flex-wrap items-baseline gap-3 mb-4">
          <h1 className="text-2xl font-semibold text-white">Team</h1>
          <span className="text-[12px] text-[#9ca3af]">
            {teamQ.isLoading
              ? "Loading…"
              : `${filtered.length} of ${teamQ.data?.technicians.length ?? 0}`}
          </span>
          <div className="ml-auto flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, postcode…"
              className="px-3 py-1.5 rounded-md bg-[#1a1a1f] border border-[#2a2a32] text-sm text-[#e5e7eb] placeholder:text-[#6b7280] focus:outline-none focus:border-[#4A154B] w-64"
            />
            <label className="flex items-center gap-1.5 text-[12px] text-[#9ca3af] cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4"
              />
              Show inactive
            </label>
          </div>
        </header>

        {teamQ.error && (
          <div className="rounded-md bg-[#3a1414] border border-[#7a2424] px-3 py-2 text-sm text-[#f5b8b8] mb-3">
            {(teamQ.error as Error).message}
          </div>
        )}

        <div className="bg-[#1a1a1f] border border-[#2a2a32] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#13131a] text-[11px] uppercase tracking-[0.08em] text-[#9ca3af]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Contact</th>
                <th className="text-left px-3 py-2 font-medium">Base</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Stripe</th>
                <th className="text-left px-3 py-2 font-medium">Insurance</th>
                <th className="text-left px-3 py-2 font-medium">Last job</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={`border-t border-[#2a2a32] hover:bg-[#1f1f26] cursor-pointer ${
                    selectedId === t.id ? "bg-[#1f1f26]" : ""
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className="text-[#e5e7eb] font-medium">
                      {t.nickName || `${t.firstName ?? ""} ${t.lastName ?? ""}`.trim() || `#${t.id}`}
                    </div>
                    <div className="text-[11px] text-[#6b7280]">#{t.id}</div>
                  </td>
                  <td className="px-3 py-2 text-[12px] text-[#9ca3af]">
                    <div className="truncate max-w-[180px]">{t.email ?? "—"}</div>
                    <div className="text-[11px]">{t.mobile ?? "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-[#9ca3af]">{t.defaultLocation ?? "—"}</td>
                  <td className="px-3 py-2">
                    <StatusPill text={t.status} />
                  </td>
                  <td className="px-3 py-2">
                    <StripePill
                      status={t.stripeConnectStatus}
                      payouts={t.stripeConnectPayoutsEnabled === 1}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <InsurancePill status={t.insuranceStatus} expiry={t.insuranceExpiry} />
                  </td>
                  <td className="px-3 py-2 text-[12px] text-[#9ca3af] tabular-nums">
                    {t.lastVisitDate ?? "never"}
                  </td>
                </tr>
              ))}
              {!teamQ.isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-[#6b7280] text-sm">
                    No technicians match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedId != null && (
        <DetailDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function StatusPill({ text }: { text: string | null }) {
  const t = (text ?? "").toLowerCase();
  const tone =
    t === "active"
      ? { bg: "#0f3a1d", fg: "#86efac", bd: "#1d5530" }
      : t === "inactive"
        ? { bg: "#2a2a32", fg: "#9ca3af", bd: "#3a3a44" }
        : { bg: "#2a2a32", fg: "#9ca3af", bd: "#3a3a44" };
  return (
    <span
      className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded border"
      style={{ background: tone.bg, color: tone.fg, borderColor: tone.bd }}
    >
      {text ?? "—"}
    </span>
  );
}

function StripePill({ status, payouts }: { status: string | null; payouts: boolean }) {
  if (!status || status === "not_started") {
    return <span className="text-[11px] text-[#6b7280]">not set up</span>;
  }
  if (status === "active" && payouts) {
    return (
      <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded border bg-[#0f3a1d] text-[#86efac] border-[#1d5530]">
        ready
      </span>
    );
  }
  if (status === "restricted") {
    return (
      <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded border bg-[#3a1414] text-[#fca5a5] border-[#7a2424]">
        restricted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded border bg-[#352a14] text-[#fcd34d] border-[#6b521f]">
      {status}
    </span>
  );
}

function InsurancePill({
  status,
  expiry,
}: {
  status: string | null;
  expiry: string | null;
}) {
  const s = status ?? "Required";
  const tone =
    s === "Approved"
      ? { bg: "#0f3a1d", fg: "#86efac", bd: "#1d5530" }
      : s === "Verifying"
        ? { bg: "#352a14", fg: "#fcd34d", bd: "#6b521f" }
        : s === "Disapproved"
          ? { bg: "#3a1414", fg: "#fca5a5", bd: "#7a2424" }
          : { bg: "#2a2a32", fg: "#9ca3af", bd: "#3a3a44" };
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded border"
        style={{ background: tone.bg, color: tone.fg, borderColor: tone.bd }}
      >
        {s}
      </span>
      {expiry && <span className="text-[11px] text-[#6b7280]">exp {expiry}</span>}
    </div>
  );
}

// ── Detail drawer ────────────────────────────────────────────────────────────

function DetailDrawer({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ["team", id],
    queryFn: () => fetchTechnicianDetail(id),
    staleTime: 30_000,
  });
  const patch = useMutation({
    mutationFn: (p: TeamAdminPatch) => patchTechnicianAdmin(id, p),
    onSuccess: (data) => {
      qc.setQueryData(["team", id], data);
      void qc.invalidateQueries({ queryKey: ["team"] });
    },
  });

  const t = detailQ.data?.technician;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-2xl bg-[#0f0f12] border-l border-[#2a2a32] overflow-y-auto">
        <div className="sticky top-0 bg-[#13131a] border-b border-[#2a2a32] px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-white font-semibold">
              {t?.nickName ||
                `${t?.firstName ?? ""} ${t?.lastName ?? ""}`.trim() ||
                `Tech #${id}`}
            </div>
            <div className="text-[11px] text-[#6b7280]">B_TUSERS.ID = {id}</div>
          </div>
          <button onClick={onClose} className="text-[#9ca3af] hover:text-white text-sm">
            Close
          </button>
        </div>

        {detailQ.isLoading && (
          <p className="px-5 py-4 text-sm text-[#9ca3af]">Loading…</p>
        )}
        {detailQ.error && (
          <div className="m-5 rounded-md bg-[#3a1414] border border-[#7a2424] px-3 py-2 text-sm text-[#f5b8b8]">
            {(detailQ.error as Error).message}
          </div>
        )}

        {t && (
          <div className="px-5 py-5 space-y-5">
            <Section title="Admin controls">
              <Field label="Status">
                <select
                  value={t.status ?? ""}
                  onChange={(e) => patch.mutate({ status: e.target.value })}
                  className="bg-[#1a1a1f] border border-[#2a2a32] text-[#e5e7eb] text-sm rounded px-2 py-1"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Insurance status">
                <select
                  value={t.insuranceStatus ?? "Required"}
                  onChange={(e) =>
                    patch.mutate({
                      insuranceStatus: e.target.value as TeamAdminPatch["insuranceStatus"],
                    })
                  }
                  className="bg-[#1a1a1f] border border-[#2a2a32] text-[#e5e7eb] text-sm rounded px-2 py-1"
                >
                  {INSURANCE_OPTS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <EditField
                label="Display name"
                value={t.nickName ?? ""}
                onSave={(v) => patch.mutate({ nickName: v })}
                placeholder="e.g. Stanislav SE27"
              />
              <EditField
                label="Default location"
                value={t.defaultLocation ?? ""}
                onSave={(v) => patch.mutate({ defaultLocation: v })}
                placeholder="Postcode area (e.g. N12)"
              />
              <EditField
                label="Commission plan"
                value={t.commissionPlan ?? ""}
                onSave={(v) => patch.mutate({ commissionPlan: v })}
                placeholder="e.g. 60"
              />
              <EditField
                label="Marker colour"
                value={t.markerColour ?? ""}
                onSave={(v) => patch.mutate({ markerColour: v })}
                placeholder="e.g. #1B5F8A"
              />
            </Section>

            <Section title="Contact">
              <Static label="Email" value={t.email} />
              <Static label="Mobile" value={t.mobile} />
              <Static label="WhatsApp" value={t.whatsappNumber || "(uses mobile)"} />
              <Static
                label="Login method"
                value={t.passwordHashSet ? "Email + password" : "OAuth only"}
              />
            </Section>

            <Section title="Stripe Connect">
              <Static label="Status" value={t.stripeConnectStatus} />
              <Static
                label="Charges"
                value={t.stripeConnectChargesEnabled === 1 ? "enabled" : "disabled"}
              />
              <Static
                label="Payouts"
                value={t.stripeConnectPayoutsEnabled === 1 ? "enabled" : "disabled"}
              />
              <Static label="Account ID" value={t.stripeConnectAccountId} mono />
            </Section>

            <Section title="Business">
              <Static label="Type" value={t.businessType} />
              <Static label="VAT registered" value={t.vatRegistered} />
              <Static label="Trading name" value={t.businessName} />
              <Static
                label="Address"
                value={[
                  t.businessAddressL1,
                  t.businessAddressL2,
                  t.businessAddressCity,
                  t.businessAddressCode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            </Section>

            <Section title="Insurance">
              <Static label="Insurer" value={t.insuranceInsurer} />
              <Static label="Cover" value={t.insuranceCoverValue} />
              <Static label="Expiry" value={t.insuranceExpiry} />
              <Field label="Certificate">
                {t.insuranceDocUrl ? (
                  <div className="space-y-1.5">
                    <a
                      href={t.insuranceDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#a78bfa] hover:text-[#c4b5fd] text-sm break-all"
                    >
                      {t.insuranceDocFilename ?? "View document"}
                    </a>
                    <div className="text-[11px] text-[#6b7280]">
                      Uploaded {fmtIso(t.insuranceDocUploadedAt)}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-[#6b7280]">No certificate uploaded.</span>
                )}
              </Field>
              {t.insuranceDocUrl && (
                <Field label="Quick action">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => patch.mutate({ insuranceStatus: "Approved" })}
                      disabled={patch.isPending || t.insuranceStatus === "Approved"}
                      className="px-3 py-1 text-sm rounded bg-[#0f3a1d] text-[#86efac] border border-[#1d5530] hover:bg-[#13501f] disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => patch.mutate({ insuranceStatus: "Disapproved" })}
                      disabled={patch.isPending || t.insuranceStatus === "Disapproved"}
                      className="px-3 py-1 text-sm rounded bg-[#3a1414] text-[#fca5a5] border border-[#7a2424] hover:bg-[#4d1717] disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </Field>
              )}
              {t.insuranceVerifiedBy && (
                <div className="text-[11px] text-[#6b7280] mt-1">
                  Last status change: {t.insuranceVerifiedBy} · {fmtIso(t.insuranceVerifiedAt)}
                </div>
              )}
            </Section>

            <Section title="Coverage">
              <Static label="Postcodes" value={t.locationsCovered} mono />
              <Static label="Counties" value={t.countyCovered} />
              <Static
                label="Services"
                value={
                  t.serviceCodes.length
                    ? `${t.serviceCodes.length} selected`
                    : "(none set)"
                }
              />
              <Static label="Other" value={t.servicesOther} />
            </Section>

            <Section title={`Vehicles (${t.vehicles.length})`}>
              {t.vehicles.length === 0 ? (
                <Static label="" value="(none listed)" />
              ) : (
                t.vehicles.map((v) => (
                  <div key={v.id} className="grid grid-cols-[140px_1fr] gap-3 text-sm">
                    <div className="text-[#9ca3af] font-mono">{v.reg}</div>
                    <div className="text-[#e5e7eb]">
                      {[v.make, v.colour, v.type].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                ))
              )}
            </Section>

            <Section title="Calendar sync">
              <Static
                label="Inbound ICS"
                value={t.freeBusyIcsUrl ? "configured" : "not set"}
              />
              <Static label="Last sync" value={t.freeBusyLastSync} />
              <Static label="Sync status" value={t.freeBusyStatus} />
              <Static
                label="Outbound feed"
                value={
                  t.calendarFeedToken
                    ? `dev.breeze.services/calendars/${t.calendarFeedToken}.ics`
                    : "not minted"
                }
                mono
              />
            </Section>

            <Section title="Notifications">
              <Static
                label="New job"
                value={`${t.notifyEmailNewJob === "Yes" ? "email" : ""} ${
                  t.notifySmsNewJob === "Yes" ? "sms" : ""
                } ${t.notifyWhatsappNewJob === "Yes" ? "whatsapp" : ""}`.trim() ||
                  "(none)"}
              />
              <Static
                label="Cancelled"
                value={`${t.notifyEmailCancelled === "Yes" ? "email" : ""} ${
                  t.notifySmsCancelled === "Yes" ? "sms" : ""
                } ${t.notifyWhatsappCancelled === "Yes" ? "whatsapp" : ""}`.trim() ||
                  "(none)"}
              />
              <Static
                label="Quiet hours"
                value={t.notifySmsQuietHours === "Yes" ? "9pm – 8am" : "off"}
              />
            </Section>

            <Section title="Activity">
              <Static label="Last visit" value={t.lastVisitDate} />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function fmtIso(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#6b7280] mb-2">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Static({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | number | null;
  mono?: boolean;
}) {
  const v = value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <div className="text-[#9ca3af]">{label}</div>
      <div
        className={`text-[#e5e7eb] break-words ${mono ? "font-mono text-[12px]" : ""}`}
      >
        {v}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-center text-sm">
      <div className="text-[#9ca3af]">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function EditField({
  label,
  value,
  onSave,
  placeholder,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  const dirty = local.trim() !== value.trim();
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-center text-sm">
      <div className="text-[#9ca3af]">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder={placeholder}
          className="bg-[#1a1a1f] border border-[#2a2a32] text-[#e5e7eb] text-sm rounded px-2 py-1 flex-1"
        />
        {dirty && (
          <button
            onClick={() => onSave(local.trim())}
            className="text-[12px] text-[#a78bfa] hover:text-[#c4b5fd]"
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
}
